import React, { useEffect, useState } from "react";
import {
  Sparkles,
  Loader2,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api from "../lib/api";

/**
 * "Why now?" callout — surfaces only when sentiment shifted day-over-day.
 * Uses Claude Sonnet 4.5 to generate a 2-sentence explanation tying together
 * the day's technical signals, news mood, and recent headlines.
 *
 * Renders nothing on quiet days (shift==null). On shift days renders a
 * colored pill that expands to show the AI summary + 3 key drivers.
 */
export default function WhyNowCallout({ assetType, symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!symbol || !assetType) return;
    let cancel = false;
    setLoading(true);
    api
      .get(`/sentiment/${assetType}/${symbol}/why-now`)
      .then((r) => !cancel && setData(r.data))
      .catch(() => !cancel && setData(null))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [assetType, symbol]);

  // Quiet day or unavailable → render nothing (no broken layout, no nag)
  if (loading || !data || !data.shift) return null;

  const isBullish = data.shift.kind === "bullish";
  const accent = isBullish ? "#5C8D73" : "#C66B59";
  const accentBg = isBullish ? "#5C8D7312" : "#C66B5912";
  const accentBorder = isBullish ? "#5C8D7340" : "#C66B5940";
  const Arrow = isBullish ? TrendingUp : TrendingDown;
  const magnitudeLabel = {
    major: "Major shift",
    notable: "Notable shift",
    subtle: "Subtle shift",
  }[data.shift.magnitude] || "Shift";

  return (
    <div
      data-testid="why-now-callout"
      className="rounded-xl border px-4 py-3 transition-soft"
      style={{ background: accentBg, borderColor: accentBorder }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="why-now-toggle"
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
            style={{ background: accent }}
          >
            <Arrow className="w-4 h-4 text-white" strokeWidth={2.25} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: accent }}>
              <Sparkles className="w-3 h-3" strokeWidth={2.25} />
              <span data-testid="why-now-magnitude">{magnitudeLabel}</span>
              <span className="text-[#6B7068] font-mono-ticker normal-case tracking-normal">
                {data.shift.delta >= 0 ? "+" : ""}
                {data.shift.delta.toFixed(0)} pts since {data.shift.prev_day}
              </span>
            </div>
            <div className="text-sm font-semibold text-[#2A2F2B] truncate">
              {data.shift.prev_label} → {isBullish ? "Bullish" : "Bearish"}: why now?
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-[#6B7068] shrink-0" strokeWidth={1.75} />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#6B7068] shrink-0" strokeWidth={1.75} />
        )}
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t space-y-2.5 text-sm" style={{ borderColor: accentBorder }} data-testid="why-now-body">
          {data.summary ? (
            <p className="text-[#2A2F2B] leading-relaxed">{data.summary}</p>
          ) : (
            <div className="flex items-center gap-2 text-[#6B7068] text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating explanation...
            </div>
          )}
          {data.key_drivers && data.key_drivers.length > 0 && (
            <ul className="space-y-1 text-xs text-[#6B7068]">
              {data.key_drivers.map((d, i) => (
                <li key={`drv-${i}`} className="flex gap-2">
                  <span style={{ color: accent }} className="mt-0.5">▸</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
